import React, { useEffect, useRef, useState } from "react";
import { addAdmin, getAllAdmins, deleteAdmin, updateAdmin } from "../api";
import { useAuth } from "../context/AuthContext";

const SuperAdmin = () => {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<any[]>([]);
  const [totalAdmins, setTotalAdmins] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>("");

  const addModalCloseRef = useRef<any>();
  const editModalCloseRef = useRef<any>();

  const [adminFormData, setAdminFormData] = useState({
    name: "",
    business: "",
    email: "",
    password: "",
  });

  const [editAdminData, setEditAdminData] = useState({
    adminId: "",
    name: "",
    business: "",
    email: "",
    password: "",
  });

  const [selectedAdmin, setSelectedAdmin] = useState<any>(null);

  // Debounce search functionality
  const debounceSearch = (() => {
    let timeoutId: NodeJS.Timeout;
    return (query: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setDebouncedSearchQuery(query);
      }, 300);
    };
  })();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    debounceSearch(e.target.value);
  };

  // Handle form input changes
  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setAdminFormData((prevData: any) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleEditInputChange = (e: any) => {
    const { name, value } = e.target;
    setEditAdminData((prevData: any) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Fetch all admins
  const fetchAdmins = async (page: number = 1, query: string = "") => {
    try {
      const response = await getAllAdmins();
      let adminsList = response?.data?.admins || [];
      
      // Filter by search query if provided
      if (query.trim()) {
        adminsList = adminsList.filter((admin: any) =>
          admin.name.toLowerCase().includes(query.toLowerCase()) ||
          admin.business.toLowerCase().includes(query.toLowerCase()) ||
          admin.email.toLowerCase().includes(query.toLowerCase())
        );
      }

      setTotalAdmins(adminsList.length);
      
      // Paginate the results
      const startIndex = (page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedAdmins = adminsList.slice(startIndex, endIndex);
      
      setAdmins(paginatedAdmins);
    } catch (error) {
      console.error("Error fetching admins:", error);
      // For demo purposes, use mock data if API fails
      const mockAdmins = [
        { id: 1, name: "John Doe", business: "Tech Corp", email: "john@techcorp.com", password: "password123" },
        { id: 2, name: "Jane Smith", business: "Finance LLC", email: "jane@finance.com", password: "secure456" },
        { id: 3, name: "Bob Johnson", business: "Marketing Inc", email: "bob@marketing.com", password: "admin789" },
      ];
      
      let filteredMockAdmins = mockAdmins;
      if (query.trim()) {
        filteredMockAdmins = mockAdmins.filter((admin: any) =>
          admin.name.toLowerCase().includes(query.toLowerCase()) ||
          admin.business.toLowerCase().includes(query.toLowerCase()) ||
          admin.email.toLowerCase().includes(query.toLowerCase())
        );
      }
      
      setTotalAdmins(filteredMockAdmins.length);
      const startIndex = (page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedAdmins = filteredMockAdmins.slice(startIndex, endIndex);
      setAdmins(paginatedAdmins);
    }
  };

  // Handle form submission for adding admin
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    
    try {
      await addAdmin(adminFormData);
      await fetchAdmins(1, debouncedSearchQuery);
      
      // Reset form
      setAdminFormData({
        name: "",
        business: "",
        email: "",
        password: "",
      });
    } catch (error) {
      console.error("Error adding admin:", error);
      // For demo purposes, add to mock data
      const newAdmin = {
        id: Date.now(),
        ...adminFormData
      };
      setAdmins(prev => [newAdmin, ...prev]);
    } finally {
      addModalCloseRef.current?.click();
    }
  };

  // Handle edit form submission
  const handleEditSubmit = async (e: any) => {
    e.preventDefault();
    
    try {
      await updateAdmin(editAdminData.adminId, {
        name: editAdminData.name,
        business: editAdminData.business,
        email: editAdminData.email,
        password: editAdminData.password,
      });
      
      await fetchAdmins(currentPage, debouncedSearchQuery);
      
      // Reset edit form
      setEditAdminData({
        adminId: "",
        name: "",
        business: "",
        email: "",
        password: "",
      });
    } catch (error) {
      console.error("Error updating admin:", error);
      // For demo purposes, update in current state
      setAdmins(prev => prev.map(admin => 
        admin.id === editAdminData.adminId 
          ? { ...admin, ...editAdminData }
          : admin
      ));
    } finally {
      editModalCloseRef.current?.click();
    }
  };

  // Handle delete admin
  const handleDeleteAdmin = async (adminId: any) => {
    if (window.confirm("Are you sure you want to delete this admin?")) {
      try {
        await deleteAdmin({ adminId });
        await fetchAdmins(currentPage, debouncedSearchQuery);
      } catch (error) {
        console.error("Error deleting admin:", error);
        // For demo purposes, remove from current state
        setAdmins(prev => prev.filter(admin => admin.id !== adminId));
      }
    }
  };

  // Handle edit click
  const handleEditClick = (admin: any) => {
    setEditAdminData({
      adminId: admin.id,
      name: admin.name,
      business: admin.business,
      email: admin.email,
      password: admin.password,
    });
  };

  // Download credentials as JSON
  const downloadCredentials = (admin: any) => {
    const credentials = {
      name: admin.name,
      business: admin.business,
      email: admin.email,
      password: admin.password,
      downloadedAt: new Date().toISOString(),
    };
    
    const dataStr = JSON.stringify(credentials, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `credentials_${admin.name.replace(/\s+/g, '_')}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // Pagination functions
  const totalPages = Math.ceil(totalAdmins / itemsPerPage);

  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Effects
  useEffect(() => {
    fetchAdmins(currentPage, debouncedSearchQuery);
  }, [currentPage, debouncedSearchQuery]);

  useEffect(() => {
    if (user) {
      fetchAdmins(1);
    }
  }, [user]);

  return (
    <div className="employee-panel">
      <div className="container-fluid">
        <div className="header d-flex justify-content-between">
          <h2>Super Admin Dashboard</h2>
          <button
            className="btn btn-danger"
            onClick={() => {
              localStorage.clear();
              window.location.href = "/";
            }}
          >
            Logout
          </button>
        </div>

        <div className="mt-4">
          <div className="card-header d-flex justify-content-between">
            <div className="heading">
              <h2>Admin Management</h2>
            </div>
            <div className="button">
              <button
                type="button"
                className="btn btn-primary"
                data-bs-target="#addAdminModal"
                data-bs-toggle="modal"
              >
                Add New Admin
              </button>
            </div>
          </div>

          <div className="search-bar" style={{width:"22%", justifySelf:"flex-end"}}>
            <input
              type="text"
              placeholder="Search admins..."
              className="form-control mb-3"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>

          <table className="table caption-top">
            <thead className="table-light">
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Business</th>
                <th scope="col">Email</th>
                <th scope="col">Password</th>
                <th scope="col" colSpan={3} className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins && admins.length > 0 ? (
                admins.map((admin: any, index: number) => (
                  <tr key={admin.id || index}>
                    <td>{admin.name}</td>
                    <td>{admin.business}</td>
                    <td>{admin.email}</td>
                    <td>{"*".repeat(admin.password?.length || 8)}</td>
                    <td>
                      <span
                        className="badge text-bg-warning cursor me-1"
                        data-bs-toggle="modal"
                        data-bs-target="#editAdminModal"
                        onClick={() => handleEditClick(admin)}
                      >
                        Edit
                      </span>
                    </td>
                    <td>
                      <span
                        className="badge text-bg-danger cursor me-1"
                        onClick={() => handleDeleteAdmin(admin.id)}
                      >
                        Delete
                      </span>
                    </td>
                    <td>
                      <span
                        className="badge text-bg-success cursor"
                        onClick={() => downloadCredentials(admin)}
                      >
                        Download
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center">
                    No admins found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <nav aria-label="Page navigation example">
            <ul className="pagination justify-content-end me-4">
              <li className="page-item">
                <a className="page-link" href="#" onClick={goToPreviousPage}>
                  &laquo; Previous
                </a>
              </li>

              {(() => {
                const pageWindow = 2;
                const pages = [];
                
                let startPage = Math.max(1, currentPage - pageWindow);
                let endPage = Math.min(totalPages, currentPage + pageWindow);

                if (startPage > 1) {
                  pages.push(
                    <li key="first" className="page-item">
                      <a
                        className="page-link"
                        href="#"
                        onClick={() => goToPage(1)}
                      >
                        1
                      </a>
                    </li>
                  );
                  
                  if (startPage > 2) {
                    pages.push(
                      <li key="ellipsis1" className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    );
                  }
                }

                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <li key={i} className="page-item">
                      <a
                        className={`page-link ${currentPage === i ? "active" : ""}`}
                        href="#"
                        onClick={() => goToPage(i)}
                      >
                        {i}
                      </a>
                    </li>
                  );
                }

                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pages.push(
                      <li key="ellipsis2" className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    );
                  }
                  
                  pages.push(
                    <li key="last" className="page-item">
                      <a
                        className="page-link"
                        href="#"
                        onClick={() => goToPage(totalPages)}
                      >
                        {totalPages}
                      </a>
                    </li>
                  );
                }

                return pages;
              })()}

              <li className="page-item">
                <a className="page-link" href="#" onClick={goToNextPage}>
                  Next &raquo;
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Add Admin Modal */}
      <div
        className="modal fade employee-task-modal"
        id="addAdminModal"
        aria-hidden="true"
        aria-labelledby="addAdminModalLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="addAdminModalLabel">
                Add New Admin
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                ref={addModalCloseRef}
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit} className="d-flex flex-column">
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">
                    Name: <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="form-control"
                    value={adminFormData.name}
                    onChange={handleInputChange}
                    placeholder="Enter admin name"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="business" className="form-label">
                    Business: <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="business"
                    name="business"
                    className="form-control"
                    value={adminFormData.business}
                    onChange={handleInputChange}
                    placeholder="Enter business name"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="email" className="form-label">
                    Email: <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-control"
                    value={adminFormData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="password" className="form-label">
                    Password: <span className="text-danger">*</span>
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    className="form-control"
                    value={adminFormData.password}
                    onChange={handleInputChange}
                    placeholder="Enter password"
                    required
                  />
                </div>

                <button type="submit" className="btn btn-primary mt-3">
                  Add Admin
                </button>
              </form>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Admin Modal */}
      <div
        className="modal fade employee-task-modal"
        id="editAdminModal"
        aria-hidden="true"
        aria-labelledby="editAdminModalLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="editAdminModalLabel">
                Edit Admin
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                ref={editModalCloseRef}
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleEditSubmit} className="d-flex flex-column">
                <div className="mb-3">
                  <label htmlFor="editName" className="form-label">
                    Name: <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="editName"
                    name="name"
                    className="form-control"
                    value={editAdminData.name}
                    onChange={handleEditInputChange}
                    placeholder="Enter admin name"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="editBusiness" className="form-label">
                    Business: <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="editBusiness"
                    name="business"
                    className="form-control"
                    value={editAdminData.business}
                    onChange={handleEditInputChange}
                    placeholder="Enter business name"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="editEmail" className="form-label">
                    Email: <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    id="editEmail"
                    name="email"
                    className="form-control"
                    value={editAdminData.email}
                    onChange={handleEditInputChange}
                    placeholder="Enter email address"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="editPassword" className="form-label">
                    Password: <span className="text-danger">*</span>
                  </label>
                  <input
                    type="password"
                    id="editPassword"
                    name="password"
                    className="form-control"
                    value={editAdminData.password}
                    onChange={handleEditInputChange}
                    placeholder="Enter password"
                    required
                  />
                </div>

                <button type="submit" className="btn btn-primary mt-3">
                  Update Admin
                </button>
              </form>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdmin; 